import React from 'react';
import { Card, Typography } from 'antd';

const { Text } = Typography;

export interface SparklinePoint {
  label: string;
  value: number;
}

export interface TrendSparklineProps {
  title: string;
  points: SparklinePoint[];
  color?: string;
  testId?: string;
}

const SPARKLINE_WIDTH = 200;
const SPARKLINE_HEIGHT = 48;

/**
 * SVG sparkline for appointment volume / utilisation trends (D-G-5).
 * Pure SVG — no charting lib.
 */
const TrendSparkline: React.FC<TrendSparklineProps> = ({
  title,
  points,
  color = '#2563eb',
  testId,
}) => {
  // A single point (e.g. the "Today" range) is still data worth showing — we
  // render it as a dot. Only an empty array is truly "no data".
  const hasData = points.length >= 1;
  const isSinglePoint = points.length === 1;

  const pathData = React.useMemo(() => {
    if (!hasData || isSinglePoint) return '';
    const maxVal = Math.max(1, ...points.map((p) => p.value));
    const minVal = Math.min(0, ...points.map((p) => p.value));
    const range = maxVal - minVal || 1;

    const coords = points.map((p, i) => {
      const x = (i / (points.length - 1)) * SPARKLINE_WIDTH;
      const y = SPARKLINE_HEIGHT - ((p.value - minVal) / range) * (SPARKLINE_HEIGHT - 4) - 2;
      return `${x},${y}`;
    });

    return `M ${coords.join(' L ')}`;
  }, [points, hasData, isSinglePoint]);

  return (
    <Card title={title} size="small" data-testid={testId}>
      {hasData ? (
        <div style={{ overflowX: 'auto' }}>
          <svg
            width="100%"
            viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
            preserveAspectRatio="none"
            style={{ display: 'block', minWidth: 120 }}
            aria-label={`${title} trend chart`}
          >
            {isSinglePoint ? (
              // Center a visible dot so single-point ranges aren't blank.
              <circle
                cx={SPARKLINE_WIDTH / 2}
                cy={SPARKLINE_HEIGHT / 2}
                r={3}
                fill={color}
              />
            ) : (
              <path
                d={pathData}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
          </svg>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 2,
            }}
          >
            <Text type="secondary" style={{ fontSize: 10 }}>
              {points[0]?.label}
            </Text>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {points[points.length - 1]?.label}
            </Text>
          </div>
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>No data for this range.</Text>
      )}
    </Card>
  );
};

export default TrendSparkline;
